import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { formatMoney, signClass } from '../lib/format';
import { Select } from './ui';

/** Consolidated totals of my accounts in a reporting currency (D10), with the rate date (INT-ACC-06). */

interface Totals {
  currency: string;
  currencies: string[];
  defaultCurrency: string;
  accounts: number;
  equity: string;
  realisedToday: string;
  openRisk: string;
  rateDate: string | null;
  converted: boolean;
  unconverted: string[];
  attribution: { text: string; url: string };
}

const day = (d: string) => new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(`${d}T00:00:00Z`));

export function MyTotals() {
  const [ccy, setCcy] = useState<string | null>(null);
  const { data } = useQuery({ queryKey: ['my-totals', ccy ?? 'default'], queryFn: () => api<Totals>(`/dashboards/my-totals${ccy ? `?currency=${ccy}` : ''}`), refetchInterval: 60_000 });
  if (!data || data.accounts < 2) return null;
  const c = data.currency;
  return (
    <div className="flex flex-col gap-1.5 rounded-lg border border-[#22385a] bg-accent-bg/40 p-3" aria-label="All my accounts together">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs uppercase tracking-[0.06em] text-muted">All {data.accounts} accounts</span>
        <div className="w-[96px]">
          <Select aria-label="Show totals in" value={c} onChange={(e) => setCcy(e.target.value)} className="py-1.5">
            {data.currencies.map((x) => (
              <option key={x} value={x}>
                {x}
              </option>
            ))}
          </Select>
        </div>
      </div>
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <span className="font-mono text-lg font-semibold">{formatMoney(data.equity, c)}</span>
        <span className="text-sm">
          <span className="text-muted">Realised today </span>
          <span className={`font-semibold ${signClass(data.realisedToday)}`}>{formatMoney(data.realisedToday, c, { signed: true })}</span>
        </span>
      </div>
      <span className="text-xs text-muted">
        {data.converted && data.rateDate ? (
          <>
            Converted at rates of {day(data.rateDate)} ·{' '}
            <a href={data.attribution.url} target="_blank" rel="noreferrer">
              {data.attribution.text}
            </a>
          </>
        ) : (
          'No conversion needed'
        )}
        {data.unconverted.length ? ` · no rate for ${data.unconverted.join(', ')}` : ''}
      </span>
    </div>
  );
}
